import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { getHomeFeaturedCasesV3 } from '$/domains/caseV3/caseV3Service';
import useFeaturedListPagination from '$/domains/caseV3/useFeaturedListPagination';
import type {
  V3CanvasHomeFeaturedListData,
  V3CanvasHomeFeaturedListParams,
} from '$/domains/caseV3/type';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import FeaturedCaseGallery from './index';
import type {
  FeaturedCaseGalleryItem,
  FeaturedCaseGalleryProps,
} from './index';
import { mapFeaturedCasesToGalleryItems } from './adapter';
import styles from './Connected.module.scss';

const DEFAULT_FEATURED_CASE_LIMIT = 20;

export interface ConnectedFeaturedCaseGalleryProps
  extends Omit<FeaturedCaseGalleryProps, 'items'> {
  requestParams?: V3CanvasHomeFeaturedListParams;
  loadingFallback?: React.ReactElement | null;
  emptyFallback?: React.ReactElement | null;
  errorFallback?: React.ReactElement | null;
  onLoaded?: (
    data: V3CanvasHomeFeaturedListData,
    items: FeaturedCaseGalleryItem[],
  ) => void;
  onLoadError?: (error: unknown) => void;
}

/** 匿名拉取指定投放位的精选列表，并将接口数据交给纯展示瀑布流组件。 */
function ConnectedFeaturedCaseGallery({
  requestParams,
  loadingFallback = null,
  emptyFallback = null,
  errorFallback = null,
  onLoaded,
  onLoadError,
  title,
  labels,
  className,
  embedded,
  creatingFeaturedId,
  onCaseClick,
  onCaseExposed,
  onCastIpClick,
  onCreateSameClick,
}: ConnectedFeaturedCaseGalleryProps) {
  const {
    placement = 'website_home',
    region,
    locale,
    category_id: categoryId,
    limit = DEFAULT_FEATURED_CASE_LIMIT,
  } = requestParams ?? {};
  const languageCode = useLanguageStore((state) => state.languageStore.languageCode);
  const translate = useLanguageStore((state) => state.languageStore.smartcatTranslationText);
  const currentCountryCode = useOfficialStore((state) => state.currentCountryCode);
  const isOverseaServer = useOfficialStore((state) => state.isOverseaServer);
  const {
    list, responseLocale, loading, error, loadingMore, moreError, hasMore, nextCursor, loadMore,
  } = useFeaturedListPagination({
    placement, region, locale, category_id: categoryId, limit,
  }, getHomeFeaturedCasesV3, JSON.stringify([languageCode, currentCountryCode, isOverseaServer]));
  const items = useMemo(() => mapFeaturedCasesToGalleryItems(list).map((item) => ({
    ...item, categoryId: categoryId === undefined ? undefined : String(categoryId),
  })), [list, categoryId]);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadedRef = useRef(onLoaded);
  const onLoadErrorRef = useRef(onLoadError);

  onLoadedRef.current = onLoaded;
  onLoadErrorRef.current = onLoadError;

  /** 已载入的多页列表作为一个整体同步给首页跨路由案例队列。 */
  useEffect(() => {
    if (loading || error) return;
    onLoadedRef.current?.({
      list, locale: responseLocale, has_more: hasMore, next_cursor: nextCursor,
    }, items);
  }, [loading, error, list, responseLocale, hasMore, nextCursor, items]);

  /** 首页触底时续取一页；分页失败后不再自动观察，避免重复请求。 */
  useEffect(() => {
    const target = sentinelRef.current;
    if (!target || !hasMore || loadingMore || moreError || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) loadMore();
    }, { root: null, rootMargin: '240px', threshold: 0 });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, moreError, loadMore, items.length]);

  /** 仅向调用方报告首屏失败；分页失败保留已展示内容。 */
  useEffect(() => {
    if (error) onLoadErrorRef.current?.(error);
  }, [error]);

  if (loading) {
    return loadingFallback;
  }
  if (error) {
    return errorFallback;
  }
  if (items.length === 0 && !hasMore) {
    return emptyFallback;
  }

  return (
    <>
      <FeaturedCaseGallery
        items={items}
        title={title}
        labels={labels}
        className={className}
        embedded={embedded}
        creatingFeaturedId={creatingFeaturedId}
        onCaseClick={onCaseClick}
        onCaseExposed={onCaseExposed}
        onCastIpClick={onCastIpClick}
        onCreateSameClick={onCreateSameClick}
      />
      {hasMore && !moreError && <div ref={sentinelRef} className={styles.sentinel} aria-hidden />}
      {loadingMore && <div className={styles.pageStatus} role="status">{translate((value) => value.RoboNeo_Web_loading)}</div>}
      {moreError && <div className={styles.pageStatus} role="alert">{translate((value) => value.loading_failed_please_try_again_later)}</div>}
    </>
  );
}

export default memo(ConnectedFeaturedCaseGallery);
